import React, { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Button, Card, CardContent, ThemeOptions, Tooltip, Typography } from '@material-ui/core';

import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';

import { ICurator } from 'core/context/admin/curator-listing';
import { FileSize } from 'core/enums';
import { AppRoutes, getPhotoUrl } from 'core/helpers';
import { EditUserAccessModal } from '../../_dashboard/EditUserAccessModal/EditUserAccessModal';
import { Link } from 'react-router-dom';
import { ConfirmUserEmailModal } from 'components/_dashboard/ConfirmUserEmailModal/ConfirmUserEmailModal';

const useStyles = makeStyles((theme: ThemeOptions) => ({
    root: {
        border: '1px solid',
        borderColor: theme.divider.main,
        borderRadius: 2,
    },
    profileIconPlaceholder: {
        width: 40,
        height: 40,
        color: '#FE6502',
    },
    profilePic: {
        width: 40,
        height: 40,
        borderRadius: 40,
    },
    name: {
        fontSize: 16,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    email: {
        fontSize: 12,
        wordBreak: 'break-all',
    },
    totalObjects: {
        fontSize: 16,
        fontWeight: 'bold',
        marginTop: 7,
    },
    expertReviews: {
        fontSize: 13,
    },
    divider: {
        width: '85%',
        margin: '7px auto',
        borderTop: '1px solid',
        borderColor: theme.divider.main,
    },
    typeLabel: {
        fontSize: 12,
        color: theme.label.text,
        backgroundColor: theme.label.main,
        borderRadius: 2,
        padding: '2px 7px',
    },
    activeIcon: {
        color: '#0F940A',
        width: 22,
    },
    inactiveIcon: {
        color: '#FF0000',
        width: 22,
    },
    activateEmailButton: {
        fontSize: 12,
    },
}));

type CuratorCardProps = {
    curator: ICurator;
};

export const CuratorCard: React.FC<CuratorCardProps> = ({ curator }) => {
    const classes = useStyles();

    const [isEnabled, setIsEnabled] = useState(curator.isActive);
    const [displayManageAccessModal, toggleManageAccessModal] = useState({ display: false, enable: false });
    const [emailConfirmed, setEmailConfirmed] = useState<boolean>(curator.emailConfirmed);
    const [displayConfirmEmailModal, setDisplayConfirmEmailModal] = useState<boolean>(false);

    const handleConfirmEmailClose = (): void => {
        setDisplayConfirmEmailModal(false);
    };

    const handleConfirmEmailOpen = (): void => {
        setDisplayConfirmEmailModal(true);
    };

    const onConfirmEmailSuccess = (): void => {
        setEmailConfirmed(true);
    };

    return (
        <Card className={`${classes.root} root object-card-root collector-card`} id={`${curator.userId}`}>
            <CardContent>
                <Box display={'flex'} justifyContent={'flex-end'} alignItems={'center'}>
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                        {isEnabled && (
                            <CheckCircleIcon
                                style={{ cursor: 'pointer' }}
                                className={classes.activeIcon}
                                onClick={() => toggleManageAccessModal({ display: true, enable: false })}
                            />
                        )}
                        {!isEnabled && (
                            <CancelIcon
                                style={{ cursor: 'pointer' }}
                                className={classes.inactiveIcon}
                                onClick={() => toggleManageAccessModal({ display: true, enable: true })}
                            />
                        )}
                    </Box>
                </Box>

                <Box textAlign={'center'} marginTop={'7px'}>
                    {!curator.photoUrl && <AccountCircleIcon className={classes.profileIconPlaceholder} />}
                    {curator.photoUrl && (
                        <img
                            src={getPhotoUrl(curator.photoUrl || '', FileSize.SM) || ''}
                            className={classes.profilePic}
                            alt={curator.firstName}
                        />
                    )}

                    <Tooltip
                        arrow
                        interactive={true}
                        title={`${curator.firstName} ${curator.lastName}`}
                        className="full-artifact-name-tooltip"
                    >
                        <Typography color="textPrimary" className={classes.name}>
                            {curator.firstName} {curator.lastName}
                        </Typography>
                    </Tooltip>

                    <Typography color="textSecondary" className={classes.email}>
                        {curator.email}
                    </Typography>
                    <Typography color="textSecondary" className={classes.email}>
                        {curator.phoneNumber}
                    </Typography>

                    <Typography color="textPrimary" className={classes.totalObjects}>
                        {curator.totalActiveJobs} active job{curator.totalActiveJobs !== 1 ? 's' : ''}
                    </Typography>

                    <div className={classes.divider} />

                    <Link to={`${AppRoutes.ADMIN_CURATOR_PROFILE}?id=${curator.userId}`} color="primary">
                        <Typography color="primary">View Profile</Typography>
                    </Link>

                    {!emailConfirmed && (
                        <Box width={'100%'} display="flex" justifyContent={'space-around'} alignItems="center">
                            <span style={{ fontSize: 12, color: 'red', marginRight: 5 }}>Email not confirmed</span>

                            <Button
                                className={classes.activateEmailButton}
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleConfirmEmailOpen}
                            >
                                Activate Email
                            </Button>
                        </Box>
                    )}
                </Box>

                <EditUserAccessModal
                    open={displayManageAccessModal.display}
                    userId={curator.userId}
                    enable={displayManageAccessModal.enable}
                    handleClose={() => toggleManageAccessModal({ display: false, enable: false })}
                    onSuccess={() => setIsEnabled(displayManageAccessModal.enable)}
                />

                <ConfirmUserEmailModal
                    isOpen={displayConfirmEmailModal}
                    userId={curator.userId}
                    onClose={handleConfirmEmailClose}
                    name={`${curator.firstName} ${curator.lastName}`}
                    onSuccess={onConfirmEmailSuccess}
                />
            </CardContent>
        </Card>
    );
};
